import { createBrowserRouter } from "react-router-dom";
import React, { lazy, Suspense } from "react";
// layouts
import MainLayout from "../components/layout/MainLayout";
// protected routes
import ProtectedRoutes from "../components/protectedRoutes/ProtectedRoutes";
// pages
import Home from "../pages/home/Home";
import NotFound from "../pages/not-found/NotFound";
// lazy pages
const Login = lazy(() => import("../pages/login/Login"));
// users
const Users = lazy(() => import("../pages/users/Users"));
const EditUser = lazy(() => import("../pages/users/EditUser"));
// host-registration
const HostRegistration = lazy(
  () => import("../pages/host-registration/HostRegistration")
);
const HostRegistrationDetails = lazy(
  () => import("../pages/host-registration/HostRegistrationDetails")
);
// hosts
const Hosts = lazy(() => import("../pages/hosts/Hosts"));
const EditHost = lazy(() => import("../pages/hosts/EditHost"));
// cities
const Cities = lazy(() => import("../pages/cities/Cities"));
const AddCity = lazy(() => import("../pages/cities/AddCity"));
const EditCity = lazy(() => import("../pages/cities/EditCity"));
// areas
const Areas = lazy(() => import("../pages/areas/Areas"));
const AddArea = lazy(() => import("../pages/areas/AddArea"));
const EditArea = lazy(() => import("../pages/areas/EditArea"));
// payment methods
const PaymentMethods = lazy(
  () => import("../pages/payment-methods/PaymentMethods")
);
const AddPaymentMethod = lazy(
  () => import("../pages/payment-methods/AddPaymentMethod")
);
const EditPaymentMethod = lazy(
  () => import("../pages/payment-methods/EditPaymentMethod")
);
// property types
const PropertyTypes = lazy(
  () => import("../pages/property-types/PropertyTypes")
);
const AddPropertyType = lazy(
  () => import("../pages/property-types/AddPropertyType")
);
const EditPropertyType = lazy(
  () => import("../pages/property-types/EditPropertyType")
);
// property utils
const PropertyAmenities = lazy(
  () => import("../pages/property-amenities/PropertyAmenities")
);
const AddPropertyAmenity = lazy(
  () => import("../pages/property-amenities/AddPropertyAmenity")
);
const EditPropertyAmenity = lazy(
  () => import("../pages/property-amenities/EditPropertyAmenity")
);
// guest types
const GuestTypes = lazy(() => import("../pages/guest-types/GuestTypes"));
const AddGuestType = lazy(() => import("../pages/guest-types/AddGuestType"));
const EditGuestType = lazy(() => import("../pages/guest-types/EditGuestType"));
// unit types
const UnitTypes = lazy(() => import("../pages/unit-types/UnitTypes"));
const AddUnitType = lazy(() => import("../pages/unit-types/AddUnitType"));
const EditUnitType = lazy(() => import("../pages/unit-types/EditUnitType"));
// offers
const Offers = lazy(() => import("../pages/offers/Offers"));
const ShowOffer = lazy(() => import("../pages/offers/ShowOffer"));
const EditOffer = lazy(() => import("../pages/offers/EditOffer"));
// reservations
const Reservastions = lazy(() => import("../pages/reservations/Reservastions"));
const AddReservation = lazy(
  () => import("../pages/reservations/AddReservation")
);
const EditReservation = lazy(
  () => import("../pages/reservations/EditReservation")
);
// settlements
const Settlements = lazy(() => import("../pages/settlements/Settlements"));
const ShowSettlements = lazy(
  () => import("../pages/settlements/ShowSettlements")
);
const AddSettlements = lazy(
  () => import("../pages/settlements/AddSettlements")
);
// transactions
const Transactions = lazy(() => import("../pages/transactions/Transactions"));
const ShowTransaction = lazy(
  () => import("../pages/transactions/ShowTransaction")
);
// settings
const Settings = lazy(() => import("../pages/settings/Settings"));
// notifications 
const Notifications = lazy(() => import("../pages/notifications/Notifications"))
const PushNotification = lazy(() => import("../pages/notifications/PushNotification"));
// all routes
const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProtectedRoutes>
        <MainLayout />
      </ProtectedRoutes>
    ),
    children: [
      // index home
      {
        index: true,
        element: <Home />,
      },
      // users
      {
        path: "users",
        children: [
          {
            index: true,
            element: <Users />,
          },
          {
            path: ":userId",
            element: <EditUser />,
          },
        ],
      },
      // host-registration
      {
        path: "host-registration",
        children: [
          {
            index: true,
            element: <HostRegistration />,
          },
          {
            path: ":requestId",
            element: <HostRegistrationDetails />,
          },
        ],
      },
      // hosts
      {
        path: "hosts",
        children: [
          {
            index: true,
            element: <Hosts />,
          },
          {
            path: ":hostId",
            element: <EditHost />,
          },
        ],
      },
      // cities
      {
        path: "cities",
        children: [
          {
            index: true,
            element: <Cities />,
          },
          {
            path: ":cityId",
            element: <EditCity />,
          },
          {
            path: "add",
            element: <AddCity />,
          },
        ],
      },
      // areas
      {
        path: "areas",
        children: [
          {
            index: true,
            element: <Areas />,
          },
          {
            path: ":areaId",
            element: <EditArea />,
          },
          {
            path: "add",
            element: <AddArea />,
          },
        ],
      },
      // payment-methods
      {
        path: "payment-methods",
        children: [
          {
            index: true,
            element: <PaymentMethods />,
          },
          {
            path: "add",
            element: <AddPaymentMethod />,
          },
          {
            path: ":categoryId",
            element: <EditPaymentMethod />,
          },
        ],
      },
      // property-types
      {
        path: "property-types",
        children: [
          {
            index: true,
            element: <PropertyTypes />,
          },
          {
            path: "add",
            element: <AddPropertyType />,
          },
          {
            path: ":categoryId",
            element: <EditPropertyType />,
          },
        ],
      },
      // guest- ypes
      {
        path: "guest-types",
        children: [
          {
            index: true,
            element: <GuestTypes />,
          },
          {
            path: "add",
            element: <AddGuestType />,
          },
          {
            path: ":categoryId",
            element: <EditGuestType />,
          },
        ],
      },
      // unit types
      {
        path: "unit-types",
        children: [
          {
            index: true,
            element: <UnitTypes />,
          },
          {
            path: "add",
            element: <AddUnitType />,
          },
          {
            path: ":categoryId",
            element: <EditUnitType />,
          },
        ],
      },
      // property-amenities
      {
        path: "property-amenities",
        children: [
          {
            index: true,
            element: <PropertyAmenities />,
          },
          {
            path: "add",
            element: <AddPropertyAmenity />,
          },
          {
            path: ":categoryId",
            element: <EditPropertyAmenity />,
          },
        ],
      },
      // offers
      {
        path: "offers",
        children: [
          {
            index: true,
            element: <Offers />,
          },
          {
            path: ":offerId",
            element: <EditOffer />,
          },
          {
            path: ":offerId/show",
            element: <ShowOffer />,
          },
        ],
      },
      // reservations
      {
        path: "reservations",
        children: [
          {
            index: true,
            element: <Reservastions />,
          },
          {
            path: ":reservationId",
            element: <EditReservation />,
          },
          {
            path: "add",
            element: <AddReservation />,
          },
        ],
      },
      // settlements
      {
        path: "settlements",
        children: [
          {
            index: true,
            element: <Settlements />,
          },
          {
            path: ":settlementId/show",
            element: <ShowSettlements />,
          },
          {
            path: "add",
            element: <AddSettlements />,
          },
        ],
      },
      // transactions
      {
        path: "transactions",
        children: [
          {
            index: true,
            element: <Transactions />,
          },
          {
            path: ":transactionId/show",
            element: <ShowTransaction />,
          },
        ],
      },
      // notifications
      {
        path: "notifications",
        children: [
          {
            index: true,
            element: <Notifications />,
          },
          {
            path: "push",
            element: <PushNotification />,
          },
        ],
      },
      // settings
      {
        path: "settings",
        children: [
          {
            index: true,
            element: <Settings />,
          },
        ],
      },
    ],
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);
export default router;
